import { Stack } from '@mui/material'
import { ContentChip, Modal, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { ExternalAppraiserStepOne } from 'ClientPortal/src/pages/Orders/OrderDetails/AppraiserSection/ExternalAppraiserStepOne'
import {
    ExternalAppraiserFormValues,
    ExternalAppraiserStepTwo,
} from 'ClientPortal/src/pages/Orders/OrderDetails/AppraiserSection/ExternalAppraiserStepTwo'
import { useCreateExternalParticipant } from 'ClientPortal/src/api/Queries/orders'
import { useTranslation } from 'react-i18next'

type ExternalAppraiserModalProps = {
    isOpen: boolean
    onClose: () => void
    orderId: string
    hasAsset: boolean
}

const DEFAULT_EXTERNAL_APPRAISER_FORM_VALUES: ExternalAppraiserFormValues = {
    salutation: '',
    firstName: '',
    lastName: '',
    organization_name: '',
    email: '',
}

export function ExternalAppraiserModal({ isOpen, onClose, orderId, hasAsset }: ExternalAppraiserModalProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const [isFirstStep, setIsFirstStep] = useState(true)
    const { control, formState, handleSubmit, reset } = useForm<ExternalAppraiserFormValues>({
        defaultValues: DEFAULT_EXTERNAL_APPRAISER_FORM_VALUES,
    })
    const closeAndReset = () => {
        setIsFirstStep(true)
        reset(DEFAULT_EXTERNAL_APPRAISER_FORM_VALUES)
        onClose()
    }

    const { createExternalParticipant, isCreatingExternalParticipant: isPending } = useCreateExternalParticipant({
        orderId,
        onSuccess: closeAndReset,
    })

    const handleSaveExternalParticipant = handleSubmit(({ email, lastName, salutation, organization_name, firstName }) =>
        createExternalParticipant({
            email: email.trim(),
            salutation,
            lastName: lastName.trim(),
            firstName: firstName.trim() || undefined,
            organization_name: organization_name.trim() || undefined,
        })
    )

    const handlePrimaryButtonClick = () => {
        if (isFirstStep) {
            setIsFirstStep(false)
            return
        }

        void handleSaveExternalParticipant()
    }

    const handleSecondaryButtonClick = () => {
        if (isFirstStep) {
            closeAndReset()
        } else {
            setIsFirstStep(true)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeAndReset}
            showCloseIcon={!isPending}
            closeIconTooltip={tCommonActions('close')}
            showLoading={isPending}
            disableBackdropClose={isPending}
            width="700px"
            title={
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                    <Typography variant="headline1" color={tokens.ColorTextBoldest}>
                        {t('modal.title')}
                    </Typography>
                    <ContentChip size="small" color="purple" label={t('beta')} />
                </Stack>
            }
            footerProps={{
                primaryButton: {
                    text: isFirstStep ? t('modal.setup') : tCommonActions('invite'),
                    onClick: handlePrimaryButtonClick,
                    disabled: isPending || (isFirstStep && !hasAsset),
                },
                secondaryButton: !isPending
                    ? {
                          text: isFirstStep ? t('cancel') : t('back'),
                          onClick: handleSecondaryButtonClick,
                          position: isFirstStep ? 'right' : 'left',
                      }
                    : undefined,
            }}
        >
            {isFirstStep ? (
                <ExternalAppraiserStepOne hasAsset={hasAsset} />
            ) : (
                <ExternalAppraiserStepTwo control={control} errors={formState.errors} disabled={isPending} />
            )}
        </Modal>
    )
}
